import { Box, Button, Flex, HStack, IconButton, Text } from '@chakra-ui/react';
import {
  faChevronDown,
  faChevronRight,
  faCompress,
  faExpand,
} from '@fortawesome/free-solid-svg-icons';
import is from '@sindresorhus/is';
import { Icon } from '@ui/components/Icon';
import { useEffect, useMemo, useState } from 'react';
import type { JsonPrimitive, JsonValue } from 'type-fest';

import { useJsonTreeStore } from './context';
import { JsonTreeStoreProvider } from './wrapper';

const ICON_PLACEHOLDER_WIDTH = '1.25rem';
const DEFAULT_RENDER_CHUNK_SIZE = 200;

type JsonTreeProps = {
  value: JsonValue;
  label?: string;
  startExpanded?: boolean;
  maxHeight?: string | number;
  renderChunkSize?: number;
};

export function JsonTree({
  value,
  label,
  startExpanded = false,
  maxHeight,
  renderChunkSize = DEFAULT_RENDER_CHUNK_SIZE,
}: JsonTreeProps) {
  return (
    <JsonTreeStoreProvider startExpanded={startExpanded}>
      <JsonTreeContent
        value={value}
        label={label}
        maxHeight={maxHeight}
        renderChunkSize={renderChunkSize}
      />
    </JsonTreeStoreProvider>
  );
}

type JsonTreeContentProps = {
  value: JsonValue;
  label?: string;
  maxHeight?: string | number;
  renderChunkSize: number;
};

function JsonTreeContent({
  value,
  label,
  maxHeight,
  renderChunkSize,
}: JsonTreeContentProps) {
  const forceExpandAll = useJsonTreeStore((state) => state.forceExpandAll);
  const forceCollapseAll = useJsonTreeStore((state) => state.forceCollapseAll);

  return (
    <Box fontFamily="mono" fontSize="xs" position="relative">
      <HStack
        spacing={1}
        position="absolute"
        top={2}
        right={2}
        bg="white"
        borderRadius="md"
        boxShadow="sm"
        p={1}
        zIndex={1}
      >
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Expand all nodes"
          title="Expand all"
          icon={<Icon icon={faExpand} w={3} h={3} color="snap.gray.700" />}
          onClick={forceExpandAll}
        />
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Collapse all nodes"
          title="Collapse all"
          icon={<Icon icon={faCompress} w={3} h={3} color="snap.gray.700" />}
          onClick={forceCollapseAll}
        />
      </HStack>

      <Box overflowX="auto" overflowY="auto" maxHeight={maxHeight}>
        <Box p={3}>
          <JsonNode
            nodeId="root"
            label={label}
            value={value}
            depth={0}
            renderChunkSize={renderChunkSize}
          />
        </Box>
      </Box>
    </Box>
  );
}

type JsonNodeProps = {
  nodeId: string;
  label?: string;
  value: JsonValue;
  depth: number;
  renderChunkSize: number;
};

function JsonNode({
  nodeId,
  label,
  value,
  depth,
  renderChunkSize,
}: JsonNodeProps) {
  const descriptor = useMemo(() => describeJsonValue(value), [value]);
  const isExpandable = descriptor.kind !== 'primitive';
  const isArray = descriptor.kind === 'array';
  const isObject = descriptor.kind === 'object';
  const entryCount = useMemo(() => {
    if (isArray) {
      return descriptor.entries.length;
    }

    if (isObject) {
      return Object.keys(descriptor.entries).length;
    }

    return 0;
  }, [descriptor, isArray, isObject]);

  const effectiveRenderChunkSize = Math.max(1, renderChunkSize);

  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(entryCount, effectiveRenderChunkSize),
  );

  useEffect(() => {
    setVisibleCount(Math.min(entryCount, effectiveRenderChunkSize));
  }, [entryCount, effectiveRenderChunkSize]);

  const defaultExpanded = useJsonTreeStore((state) => state.defaultExpanded);
  const nodeOverride = useJsonTreeStore((state) =>
    state.nodeStates.get(nodeId),
  );
  const setNodeExpanded = useJsonTreeStore((state) => state.setNodeExpanded);
  const removeNode = useJsonTreeStore((state) => state.removeNode);

  useEffect(() => () => removeNode(nodeId), [nodeId, removeNode]);

  const isExpanded = nodeOverride ?? defaultExpanded;

  const summaryLabel = label ? `${label}: ` : '';
  const summaryValue = isArray
    ? `Array (${entryCount})`
    : isObject
      ? `Object (${entryCount})`
      : null;

  const toggleExpansion = () => {
    if (!isExpandable) {
      return;
    }

    setNodeExpanded(nodeId, !isExpanded);
  };

  const entriesToRender = useMemo(() => {
    if (!isExpanded || !isExpandable) {
      return [];
    }

    if (isArray) {
      const limit = Math.min(visibleCount, descriptor.entries.length);

      return Array.from({ length: limit }, (_, index) => ({
        key: `[${index}]`,
        value: descriptor.entries[index],
      }));
    }

    if (isObject) {
      const keys = Object.keys(descriptor.entries).slice(0, visibleCount);

      return keys.map((key) => ({
        key,
        value: descriptor.entries[key],
      }));
    }

    return [];
  }, [descriptor, isExpanded, isExpandable, isArray, isObject, visibleCount]);

  const hasMoreEntries = entryCount > visibleCount;
  const remainingCount = Math.max(entryCount - visibleCount, 0);

  return (
    <Box
      pl={depth ? 4 : 0}
      borderLeft={depth ? '1px solid' : 'none'}
      borderColor="snap.gray.100"
    >
      <Flex
        {...(isExpandable ? { as: 'button', type: 'button' } : {})}
        w="full"
        align="center"
        gap={2}
        py={0.5}
        bg="transparent"
        border="none"
        cursor={isExpandable ? 'pointer' : 'default'}
        textAlign="left"
        aria-expanded={isExpandable ? isExpanded : undefined}
        onClick={isExpandable ? toggleExpansion : undefined}
      >
        {isExpandable ? (
          <Box
            width={ICON_PLACEHOLDER_WIDTH}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              icon={isExpanded ? faChevronDown : faChevronRight}
              w={3}
              h={3}
              color="snap.gray.600"
            />
          </Box>
        ) : (
          <Box width={ICON_PLACEHOLDER_WIDTH} />
        )}

        <Text as="span" fontFamily="mono" fontSize="xs" color="snap.gray.800">
          {summaryLabel}
          {isExpandable && summaryValue}
          {!isExpandable && descriptor.kind === 'primitive' && (
            <PrimitiveValue value={descriptor.primitive} />
          )}
        </Text>
      </Flex>

      {isExpandable && isExpanded && (
        <Box>
          {entriesToRender.map(({ key, value: childValue }, index) => {
            const childNodeId = `${nodeId}.${key}`;

            return (
              <JsonNode
                key={`${label ?? 'root'}-${depth}-${index}-${key}`}
                nodeId={childNodeId}
                label={key}
                value={childValue}
                depth={depth + 1}
                renderChunkSize={effectiveRenderChunkSize}
              />
            );
          })}

          {hasMoreEntries && (
            <Box pl={ICON_PLACEHOLDER_WIDTH} pt={1}>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="teal"
                onClick={() =>
                  setVisibleCount((prev) =>
                    Math.min(prev + effectiveRenderChunkSize, entryCount),
                  )
                }
              >
                Show {Math.min(effectiveRenderChunkSize, remainingCount)} more
                {remainingCount > effectiveRenderChunkSize
                  ? ` (${remainingCount} remaining)`
                  : ''}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

type JsonValueDescriptor =
  | { kind: 'primitive'; primitive: JsonPrimitive }
  | { kind: 'array'; entries: JsonValue[] }
  | { kind: 'object'; entries: Record<string, JsonValue> };

function describeJsonValue(value: JsonValue): JsonValueDescriptor {
  if (Array.isArray(value)) {
    return { kind: 'array', entries: value };
  }

  if (is.plainObject(value)) {
    return { kind: 'object', entries: value as Record<string, JsonValue> };
  }

  return { kind: 'primitive', primitive: value as JsonPrimitive };
}

type PrimitivePresentation = {
  value: string;
  typeLabel: string;
  color: string;
};

type PrimitiveValueProps = {
  value: JsonPrimitive;
};

function PrimitiveValue({ value }: PrimitiveValueProps) {
  const presentation = describePrimitive(value);

  return (
    <>
      <Text as="span" color={presentation.color}>
        {presentation.value}
      </Text>
      <Text as="span" color="snap.gray.500">
        {` (${presentation.typeLabel})`}
      </Text>
    </>
  );
}

function describePrimitive(value: JsonPrimitive): PrimitivePresentation {
  if (value === null) {
    return { value: 'null', typeLabel: 'null', color: 'snap.gray.600' };
  }

  if (value === undefined) {
    return {
      value: 'undefined',
      typeLabel: 'undefined',
      color: 'snap.gray.400',
    };
  }

  if (typeof value === 'string') {
    return { value: `"${value}"`, typeLabel: 'string', color: 'snap.teal.600' };
  }

  if (typeof value === 'number') {
    return { value: String(value), typeLabel: 'number', color: 'mw.orange' };
  }

  if (typeof value === 'boolean') {
    return {
      value: String(value),
      typeLabel: 'boolean',
      color: 'snap.red.600',
    };
  }

  return {
    value: String(value),
    typeLabel: typeof value,
    color: 'snap.gray.800',
  };
}
